import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/admin/BookingsTab";
import { DollarSign, Car, CheckCircle2, Clock, TrendingUp, Filter, Sparkles, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const Bar = ({ pct, tone = "amber" }) => (
  <div className="w-full bg-black/40 rounded-full h-1.5 overflow-hidden">
    <div
      className={tone === "emerald" ? "bg-emerald-400 h-full" : "bg-amber-400 h-full"}
      style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
    />
  </div>
);

function CustomFieldBreakdown() {
  const [meta, setMeta] = useState(null);
  const [selKey, setSelKey] = useState(""); // "<service_id>::<field_key>"
  const [selValue, setSelValue] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports/custom-fields");
        setMeta(data);
      } catch { toast.error("Failed to load custom fields"); }
    })();
  }, []);

  const currentField = () => {
    if (!meta || !selKey) return null;
    const [sid, key] = selKey.split("::");
    return meta.fields.find((f) => f.service_id === sid && f.field_key === key) || null;
  };

  const apply = async () => {
    const f = currentField();
    if (!f || !selValue) { toast.error("Pick a field and value"); return; }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/reports/custom-fields/breakdown?service_id=${encodeURIComponent(f.service_id)}&field_key=${encodeURIComponent(f.field_key)}&value=${encodeURIComponent(selValue)}`
      );
      setResult(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const clear = () => { setSelKey(""); setSelValue(""); setResult(null); };

  if (!meta) return <div className="text-slate-500 text-sm">Loading…</div>;
  if (!meta.fields || meta.fields.length === 0) {
    return (
      <div className="glass-card rounded-2xl p-6 gold-border" data-testid="cfr-empty">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-400/10 border border-amber-400/20 grid place-items-center">
            <Sparkles className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-white">Custom field breakdown</h3>
            <p className="text-sm text-slate-400">No services have custom questions yet. Add one under <b>Services → Custom questions</b> to start collecting reportable answers.</p>
          </div>
        </div>
      </div>
    );
  }

  const field = currentField();

  return (
    <div className="glass-card rounded-2xl p-6 gold-border" data-testid="custom-field-reporting">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-lg bg-amber-400/10 border border-amber-400/20 grid place-items-center">
          <Filter className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-white">Custom field breakdown</h3>
          <p className="text-sm text-slate-400">See how many bookings picked each answer — filter by service and value.</p>
        </div>
      </div>

      {/* Filter row */}
      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Custom field</label>
          <select
            value={selKey}
            onChange={(e) => { setSelKey(e.target.value); setSelValue(""); }}
            className="mt-1 w-full bg-black/30 border border-white/10 rounded-md h-11 px-3 text-sm text-white"
            data-testid="cfr-field-select"
          >
            <option value="">Select a field…</option>
            {meta.fields.map((f) => (
              <option key={`${f.service_id}::${f.field_key}`} value={`${f.service_id}::${f.field_key}`}>
                {f.service_name} · {f.field_label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Value</label>
          {field && field.field_type !== "text" && field.values.length > 0 ? (
            <select
              value={selValue}
              onChange={(e) => setSelValue(e.target.value)}
              className="mt-1 w-full bg-black/30 border border-white/10 rounded-md h-11 px-3 text-sm text-white"
              data-testid="cfr-value-select"
              disabled={!selKey}
            >
              <option value="">Select…</option>
              {field.values.map((v) => (
                <option key={v.value} value={v.value}>{v.value} ({v.count})</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={selValue}
              onChange={(e) => setSelValue(e.target.value)}
              placeholder={field ? "Type an answer…" : "Pick a field first"}
              disabled={!field}
              className="mt-1 w-full bg-black/30 border border-white/10 rounded-md h-11 px-3 text-sm text-white disabled:opacity-50"
              data-testid="cfr-value-input"
            />
          )}
        </div>
        <div className="flex items-end gap-2">
          <Button onClick={apply} disabled={loading || !selKey || !selValue} className="btn-gold flex-1" data-testid="cfr-apply">
            {loading ? "Filtering…" : "Apply filter"}
          </Button>
          <Button onClick={clear} variant="outline" className="bg-transparent border-white/10 text-slate-300" data-testid="cfr-clear">Clear</Button>
        </div>
      </div>

      {/* Value distribution preview per selected field (before filter) */}
      {field && (
        <div className="bg-black/20 border border-white/5 rounded-lg p-4 mb-5" data-testid="cfr-distribution">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">Distribution — {field.field_label}</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {field.values.slice(0, 12).map((v) => {
              const pct = field.service_total_bookings ? (v.count / field.service_total_bookings) * 100 : 0;
              return (
                <div key={v.value} className="text-xs">
                  <div className="flex justify-between text-slate-300 mb-1">
                    <span className="font-mono truncate max-w-[70%]">{v.value}</span>
                    <span className="text-slate-400 font-mono">{v.count} · {pct.toFixed(0)}%</span>
                  </div>
                  <Bar pct={pct} />
                </div>
              );
            })}
            {field.values.length === 0 && <div className="text-slate-500 text-sm">No answers yet for this field.</div>}
          </div>
        </div>
      )}

      {/* Applied filter result */}
      {result && (
        <div className="space-y-4" data-testid="cfr-result">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-amber-400/5 border border-amber-400/20 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Matching bookings</div>
              <div className="font-display text-3xl font-extrabold text-amber-400 mt-1" data-testid="cfr-total">{result.total}</div>
            </div>
            <div className="bg-black/20 border border-white/5 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">% of all bookings</div>
              <div className="font-display text-3xl font-extrabold text-white mt-1">{result.percent_of_all}%</div>
            </div>
            <div className="bg-black/20 border border-white/5 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Filter</div>
              <div className="text-sm text-slate-300 mt-1 font-mono truncate">
                <Badge className="bg-slate-700/40 border border-slate-600/40 text-slate-300 mr-2">{field?.field_label}</Badge>
                {result.value}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <Slice title="Top drivers" rows={result.by_driver.map((r) => [r.driver, r.count])} testid="cfr-by-driver" />
            <Slice title="By status" rows={result.by_status.map((r) => [r.status, r.count])} testid="cfr-by-status" />
            <Slice title="By payment" rows={result.by_payment.map((r) => [r.payment_status, r.count])} testid="cfr-by-payment" />
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg overflow-hidden">
            <div className="p-3 border-b border-white/5 text-sm font-semibold text-white">Recent matching bookings</div>
            <div className="max-h-72 overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-black/60 backdrop-blur text-slate-500 uppercase tracking-widest text-[10px] font-mono">
                  <tr>
                    <th className="text-left px-3 py-2 font-normal">Ref</th>
                    <th className="text-left px-3 py-2 font-normal">Rider</th>
                    <th className="text-left px-3 py-2 font-normal">Route</th>
                    <th className="text-left px-3 py-2 font-normal">Status</th>
                    <th className="text-right px-3 py-2 font-normal">Fare</th>
                  </tr>
                </thead>
                <tbody>
                  {result.recent.length === 0 && (
                    <tr><td colSpan="5" className="text-center text-slate-500 py-6">No matches yet.</td></tr>
                  )}
                  {result.recent.map((b) => (
                    <tr key={b.reference} className="border-t border-white/5">
                      <td className="px-3 py-2 text-amber-300 font-mono">#{b.reference}</td>
                      <td className="px-3 py-2 text-slate-300">{b.rider_name}</td>
                      <td className="px-3 py-2 text-slate-400">{(b.pickup_location || "").slice(0, 28)} → {(b.dropoff_location || "").slice(0, 28)}</td>
                      <td className="px-3 py-2"><span className="text-slate-300 font-mono">{b.status}</span></td>
                      <td className="px-3 py-2 text-right text-white">NZ${(b.fare_estimate || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Slice = ({ title, rows, testid }) => {
  const max = Math.max(1, ...rows.map(([, n]) => n));
  return (
    <div className="bg-black/20 border border-white/5 rounded-lg p-4" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">{title}</div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-slate-500 text-sm">—</div>}
        {rows.map(([label, count]) => (
          <div key={label} className="text-xs">
            <div className="flex justify-between text-slate-300 mb-1">
              <span className="truncate max-w-[70%]">{label}</span>
              <span className="text-slate-400 font-mono">{count}</span>
            </div>
            <Bar pct={(count / max) * 100} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ReportsTab() {
  const [s, setS] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/reports/summary"); setS(data); }
      catch { toast.error("Failed"); }
    })();
  }, []);
  if (!s) return <div className="text-slate-500">Loading…</div>;
  const cards = [
    { icon: DollarSign, label: "Revenue (paid)", value: `NZ$ ${s.revenue_nzd.toFixed(2)}`, tone: "text-amber-400" },
    { icon: TrendingUp, label: "Total bookings", value: s.bookings.total, tone: "text-white" },
    { icon: Clock, label: "Pending", value: s.bookings.pending, tone: "text-amber-300" },
    { icon: CheckCircle2, label: "Completed", value: s.bookings.completed, tone: "text-emerald-400" },
    { icon: Car, label: "Drivers available", value: `${s.drivers.available}/${s.drivers.total}`, tone: "text-blue-400" },
  ];
  return (
    <div>
      <Header title="Reports" subtitle="Business at-a-glance." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="glass-card rounded-2xl p-5 gold-border" data-testid={`report-${c.label}`}>
            <c.icon className={`w-5 h-5 ${c.tone} mb-3`} />
            <div className="text-xs uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className={`font-display text-2xl font-extrabold mt-1 ${c.tone}`}>{c.value}</div>
          </div>
        ))}
      </div>
      <div className="mt-8 glass-card rounded-2xl p-6 gold-border">
        <h3 className="font-display text-lg font-bold mb-4">Booking status breakdown</h3>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
          {Object.entries(s.bookings).filter(([k]) => !["total", "paid"].includes(k)).map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.replace("_", " ")}</div>
              <div className="font-mono text-2xl text-white mt-1">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Field Reporting */}
      <div className="mt-8">
        <CustomFieldBreakdown />
      </div>

      {/* Commission Report */}
      <div className="mt-8">
        <CommissionReport />
      </div>
    </div>
  );
}

function CommissionReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/commission?year=${year}&month=${month}`);
      setData(data);
    } catch { toast.error("Failed to load commission"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const monthName = new Date(year, month - 1).toLocaleString("en", { month: "long", year: "numeric" });
  const years = [now.getFullYear() - 1, now.getFullYear()];
  const months = [
    [1, "Jan"], [2, "Feb"], [3, "Mar"], [4, "Apr"], [5, "May"], [6, "Jun"],
    [7, "Jul"], [8, "Aug"], [9, "Sep"], [10, "Oct"], [11, "Nov"], [12, "Dec"],
  ];

  const downloadCsv = () => {
    if (!data || !data.rows) return;
    const label = data.tax_label || "GST";
    const header = ["Service", "Rides", "Gross NZD", "Commission %", "Commission NZD", `${label} NZD`, "Driver Earnings NZD"];
    const rows = data.rows.map((r) => [
      r.service, r.rides, r.gross.toFixed(2), r.commission_pct.toFixed(1),
      r.commission.toFixed(2), r.tax.toFixed(2), r.driver_earnings.toFixed(2),
    ]);
    const totals = ["TOTAL", data.totals.rides, data.totals.gross.toFixed(2), "",
                    data.totals.commission.toFixed(2), data.totals.tax.toFixed(2),
                    data.totals.driver_earnings.toFixed(2)];
    const csv = [header, ...rows, totals]
      .map((row) => row.map((c) => {
        const s = String(c ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `commission-${year}-${String(month).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  return (
    <div className="glass-card rounded-2xl p-6 gold-border" data-testid="commission-report">
      <div className="flex items-start gap-3 mb-4 flex-wrap justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-400/10 border border-amber-400/20 grid place-items-center">
            <Wallet className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-white">Commission & driver earnings</h3>
            <p className="text-sm text-slate-400">Per-service breakdown for {monthName}{data?.tax_label ? ` · Tax label: ${data.tax_label}` : ""}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="bg-black/30 border border-white/10 rounded-md h-10 px-3 text-sm text-white" data-testid="commission-month">
            {months.map(([m, n]) => <option key={m} value={m}>{n}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-black/30 border border-white/10 rounded-md h-10 px-3 text-sm text-white" data-testid="commission-year">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            onClick={downloadCsv}
            disabled={!data || data.rows.length === 0}
            className="h-10 px-3 rounded-md bg-transparent border border-amber-400/40 text-amber-400 text-xs font-mono uppercase tracking-widest hover:bg-amber-400/10 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="commission-csv"
          >
            ↓ CSV
          </button>
        </div>
      </div>

      {loading ? <div className="text-slate-500 text-sm">Loading…</div> : !data ? null : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <StatCard label="Rides" value={data.totals.rides} tone="text-white" />
            <StatCard label="Gross revenue" value={`NZ$${data.totals.gross.toFixed(2)}`} tone="text-amber-400" />
            <StatCard label="Commission" value={`NZ$${data.totals.commission.toFixed(2)}`} tone="text-amber-300" />
            <StatCard label="Driver earnings" value={`NZ$${data.totals.driver_earnings.toFixed(2)}`} tone="text-emerald-400" />
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-black/40 text-slate-500 uppercase tracking-widest text-[10px] font-mono">
                <tr>
                  <th className="text-left px-3 py-2 font-normal">Service</th>
                  <th className="text-right px-3 py-2 font-normal">Rides</th>
                  <th className="text-right px-3 py-2 font-normal">Gross</th>
                  <th className="text-right px-3 py-2 font-normal">Comm %</th>
                  <th className="text-right px-3 py-2 font-normal">Commission</th>
                  <th className="text-right px-3 py-2 font-normal">{data.tax_label}</th>
                  <th className="text-right px-3 py-2 font-normal">Driver keeps</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan="7" className="text-center text-slate-500 py-6" data-testid="commission-empty">No completed rides in this month.</td></tr>
                )}
                {data.rows.map((r) => (
                  <tr key={r.service} className="border-t border-white/5" data-testid={`commission-row-${r.service}`}>
                    <td className="px-3 py-2 text-slate-200">{r.service}</td>
                    <td className="px-3 py-2 text-right text-slate-300 font-mono">{r.rides}</td>
                    <td className="px-3 py-2 text-right text-amber-300 font-mono">NZ$ {r.gross.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-slate-400 font-mono">{r.commission_pct.toFixed(0)}%</td>
                    <td className="px-3 py-2 text-right text-amber-400 font-mono">NZ$ {r.commission.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-slate-300 font-mono">NZ$ {r.tax.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-emerald-400 font-mono">NZ$ {r.driver_earnings.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const StatCard = ({ label, value, tone }) => (
  <div className="bg-black/20 border border-white/5 rounded-lg p-4">
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
    <div className={`font-display text-2xl font-extrabold mt-1 ${tone}`}>{value}</div>
  </div>
);
